import React, { FC, ReactNode, useMemo } from 'react'

import { styled } from '@linaria/react'

import { MobileNotAuthorized } from 'components/block/ControlButtonsBar/MobileNotAuthorized'
import { breakpoints } from 'components/designSystem/shared/breakpoints'
import { GQLPhotoItemFragment } from 'components/page/ForeignProfile/gql/__generated__/Photos'
import { GQLGender } from 'generated-graphql/graphql'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

import { Photos } from './block/Photos'
import { ProfileMainWidths } from '../ProfileMain/ProfileMain.constants'
import { ProfileStatusAppearScroll } from '../ProfileStatusAppearScroll/ProfileStatusAppearScroll'

export const blankSpaceHeight = 100
const observerThreshold = 0.4

/**
 * photos - только главная и портретные
 * restPhotos - все остальные кроме photos
 */
export const ProfilePhotos: FC<{
  userId: number
  gender: GQLGender
  incognito: boolean
  personalBlock: ReactNode
  photos: GQLPhotoItemFragment[]
  restPhotos: GQLPhotoItemFragment[]
  isOnline?: boolean
  hasVerified?: boolean
}> = ({
  userId,
  gender,
  incognito,
  photos,
  restPhotos,
  personalBlock,
  isOnline,
  hasVerified,
}) => {
  const { setNode, entry } = useIntersectionObserver({
    threshold: observerThreshold,
  })

  const { javaScriptEnabled } = useShallowEqualSelector(
    ({ systemReducer: { javaScriptEnabled } }) => ({
      javaScriptEnabled,
    })
  )

  const hasShowedProfileStatus = useMemo(
    () =>
      Boolean(
        entry &&
          entry.intersectionRatio &&
          entry.intersectionRatio <= observerThreshold
      ),
    [entry]
  )

  return (
    <>
      <ProfileStatusAppearScroll
        isOnline={isOnline}
        hasVerified={hasVerified}
        hasShowed={hasShowedProfileStatus}
      />
      <Parent>
        <Photos
          userId={userId}
          gender={gender}
          photos={photos}
          restPhotos={restPhotos}
          incognito={incognito}
        />
        <Name ref={setNode}>{personalBlock}</Name>

        {!javaScriptEnabled && <MobileNotAuthorized />}
      </Parent>
    </>
  )
}

const Parent = styled.div`
  display: flex;
  height: 100%;
  flex-grow: 1;
  flex-direction: column;
  overflow: hidden;
  position: sticky;
  top: 0;

  @media screen and (max-width: ${breakpoints.mobile}px) {
    overflow: visible;
    position: relative;
    min-height: 100%;
    clip-path: polygon(
      11.396% 0%,
      11.396% 0%,
      9.548% 0.082%,
      7.794% 0.32%,
      6.159% 0.7%,
      4.666% 1.21%,
      3.338% 1.837%,
      2.199% 2.567%,
      1.272% 3.389%,
      0.581% 4.288%,
      0.149% 5.253%,
      0% 6.27%,
      0% 90.608%,
      0% 90.608%,
      0.06% 91.5%,
      0.267% 92.467%,
      0.661% 93.47%,
      1.28% 94.468%,
      2.165% 95.422%,
      3.356% 96.291%,
      4.892% 97.037%,
      6.812% 97.619%,
      9.157% 97.997%,
      11.966% 98.132%,
      23.362% 98.132%,
      23.362% 98.132%,
      28.702% 98.174%,
      34.038% 98.237%,
      39.37% 98.323%,
      44.699% 98.43%,
      50.026% 98.56%,
      55.35% 98.712%,
      60.673% 98.886%,
      65.995% 99.082%,
      71.316% 99.301%,
      76.638% 99.543%,
      76.638% 99.543%,
      77.426% 99.579%,
      78.397% 99.621%,
      79.515% 99.668%,
      80.743% 99.717%,
      82.045% 99.769%,
      83.385% 99.82%,
      84.726% 99.871%,
      86.031% 99.919%,
      87.264% 99.962%,
      88.388% 100%,
      88.388% 100%,
      90.446% 99.979%,
      92.334% 99.785%,
      94.042% 99.437%,
      95.557% 98.95%,
      96.869% 98.342%,
      97.967% 97.628%,
      98.84% 96.826%,
      99.477% 95.952%,
      99.867% 95.024%,
      100% 94.056%,
      100% 6.27%,
      100% 6.27%,
      99.851% 5.253%,
      99.419% 4.288%,
      98.728% 3.389%,
      97.801% 2.567%,
      96.662% 1.837%,
      95.334% 1.21%,
      93.841% 0.7%,
      92.206% 0.32%,
      90.452% 0.082%,
      88.604% 0%,
      11.396% 0%
    );
  }

  @media screen and (min-width: ${breakpoints.mobile + 1}px) {
    max-width: ${ProfileMainWidths.smallImages}px;
    min-width: ${ProfileMainWidths.smallImages}px;
    flex-basis: ${ProfileMainWidths.smallImages}px;
    width: ${ProfileMainWidths.smallImages}px;
  }

  @media screen and (min-width: ${breakpoints.middleDesktop}px) {
    max-width: ${ProfileMainWidths.middleImages}px;
    min-width: ${ProfileMainWidths.middleImages}px;
    flex-basis: ${ProfileMainWidths.middleImages}px;
    width: ${ProfileMainWidths.middleImages}px;
  }

  @media screen and (min-width: ${breakpoints.largeDesktop}px) {
    max-width: ${ProfileMainWidths.largeImages}px;
    min-width: ${ProfileMainWidths.largeImages}px;
    flex-basis: ${ProfileMainWidths.largeImages}px;
    width: ${ProfileMainWidths.largeImages}p;
  }
`

const Name = styled.div`
  display: none;
  position: absolute;
  flex-direction: column;
  bottom: 0;
  width: 100%;
  height: max(calc(140px - var(--dynamic-scroll-y, 0px)), 80px);
  padding: var(--spacing-12px) var(--spacing-24px);
  background: linear-gradient(
    180deg,
    rgba(var(--static-black-rgb), 0) 0%,
    rgba(var(--static-black-rgb), 0.8) 100%
  );
  color: var(--static-white);

  @media screen and (max-width: ${breakpoints.mobile}px) {
    display: flex;
  }
`
/**
 * Оставлю на будущее как пример
 */
// const scrollAnimationNameCss = css`
//   @supports (animation-timeline: view(block)) {
//     animation: shrink linear both;
//     animation-timeline: view(block);
//     animation-range: contain 0% contain 20%;
//   }

//   @keyframes shrink {
//     from {
//       height: 140px;
//     }
//     to {
//       height: 80px;
//     }
//   }
// `
